import React from 'react';
import { throttle } from 'lodash-es';
import millify from 'millify';
import ms from 'ms';
import type { Address } from 'viem';
import type { MintSchedule } from '@soundxyz/sdk';
import type { Maybe } from '@soundxyz/utils/helpers';
import {
  ALLOWED_INTERFACES_FOR_GIFTING,
  GiftingDropdown,
} from '@components/Gifting/GiftingDropdown';
import { hasSaleStarted } from '@components/Song/MintComponents/MintComponent/Edition/useMintState';
import { QuantityInput, quantityReducer } from '@components/main-components/inputs/QuantityInput';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { ModalContext } from '@context/modal';
import { ParamsStore } from '@context/params';
import {
  CheckoutReleaseInfoFragmentDoc,
  type FragmentType,
  getFragment,
  gql,
} from '@graphql/reactQuery';
import { useSubscribeReleaseCollector } from '@hooks/useSubscribeRelease';
import { useEditionEligibleQuantity, useHasScheduleEligibility } from '@store/sdk/reactQuery';
import { bigIntToNumber } from '@utils/bigNumberHelpers';
import { ADDRESS_BATCH_MINT_LIMIT, SPACE } from '@utils/constants';
import { ReleaseDetails } from '../shared';
import { BuyButtons } from './BuyButtons';
import { PriceBreakdown } from './PriceBreakdown';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CheckoutReleaseInfo on Release {
    id
    chainId
    auctionContractType
    editionId
    title
    type
    contractAddress

    ...ReleaseDetails
    ...ReleaseBuyButtons
  }
`);

export const CheckoutModal = ({
  releaseData,
  schedule,
  headingText,
  selectedPanel,
  openGiftingDropdown,
}: {
  releaseData: FragmentType<CheckoutReleaseInfoFragmentDoc>;
  schedule: Maybe<MintSchedule>;
  headingText: string;
  selectedPanel: 'PUBLIC' | 'PRESALE';
  openGiftingDropdown: boolean;
}) => {
  const release = getFragment(CheckoutReleaseInfoFragmentDoc, releaseData);
  const [giftAddress, setGiftAddress] = React.useState<Address | null>(null);
  const [openGifting, setOpenGifting] = React.useState(openGiftingDropdown);
  const allowGifting =
    selectedPanel === 'PUBLIC' &&
    schedule &&
    Object.values(ALLOWED_INTERFACES_FOR_GIFTING).includes(schedule.interfaceId);

  const { chainId, id: releaseId, contractAddress, auctionContractType } = release;
  const { closeCancelModal } = ModalContext.useContainer();

  const { data: hasSaleAccess = false } = useHasScheduleEligibility({
    mintSchedule: schedule,
    editionAddress: contractAddress,
    auctionContractType,
  });

  const { data: eligibleQuantity = null, refetch: refetchEligibleQuantity } =
    useEditionEligibleQuantity({
      mintSchedule: schedule,
      chainId,
      editionAddress: contractAddress,
      giftTo: openGifting ? giftAddress : null,
    });

  const throttledRefetchEligibleQuantity = React.useMemo(
    () =>
      throttle(refetchEligibleQuantity, ms('10 seconds'), {
        leading: true,
        trailing: true,
      }),
    [refetchEligibleQuantity],
  );

  useSubscribeReleaseCollector({
    releaseId,
    onData() {
      throttledRefetchEligibleQuantity();
    },
    enabled: true,
  });

  const eligibleQuantityNum = eligibleQuantity
    ? bigIntToNumber(eligibleQuantity, { strict: false })
    : 0;

  const inputMax = eligibleQuantityNum !== 0 ? eligibleQuantityNum : 1;

  const [state, dispatch] = React.useReducer(quantityReducer, {
    value: 1,
    error: false,
  });

  if (!schedule) return null;

  const saleStarted = hasSaleStarted({ startTimestamp: schedule.startTime });

  const ctaButtons = (
    <>
      {/* TODO: https://linear.app/sound/issue/ENG-7452/add-loading-states-to-buy-buttons*/}
      {schedule ? (
        <div className="w-full">
          <BuyButtons
            releaseData={release}
            schedule={schedule}
            error={state.error}
            giftingOpened={openGifting}
            quantity={state.value || 0}
            giftAddress={allowGifting ? giftAddress : null}
          />
        </div>
      ) : null}
    </>
  );

  const checkout = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        <ReleaseDetails releaseData={release} />

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <span className="text-base-m text-base800">
              {headingText}
              {schedule.mintType === 'MerkleDrop' && (
                <span className="text-fuchsia500">{SPACE}Presale</span>
              )}
              <span className="text-base500">
                {SPACE}•{SPACE}Max{SPACE}
                {eligibleQuantity
                  ? millify(bigIntToNumber(eligibleQuantity, { strict: false }))
                  : '...'}
              </span>
            </span>
            <QuantityInput
              state={state}
              disabled={!saleStarted || !hasSaleAccess}
              min={1}
              dispatch={dispatch}
              max={inputMax}
            />
          </div>
          <PriceBreakdown
            schedule={schedule}
            quantity={state.value || 0}
            chainId={chainId}
            editionAddress={contractAddress}
          />
          {allowGifting && (
            <GiftingDropdown
              setGiftAddress={setGiftAddress}
              releaseId={releaseId}
              sourceComponent="Edition Checkout Modal"
              open={openGifting}
              setOpen={setOpenGifting}
              eligibleQuantity={
                eligibleQuantity
                  ? Math.min(
                      bigIntToNumber(eligibleQuantity, { strict: false }),
                      ADDRESS_BATCH_MINT_LIMIT,
                    )
                  : 0
              }
            />
          )}
        </div>
        {ctaButtons}
      </div>
    </div>
  );

  return (
    <StandardModal
      title="Checkout"
      body={checkout}
      closeModal={() => {
        closeCancelModal();
        setOpenGifting(false);
        ParamsStore.gift_to = null;
      }}
      bodyWithoutPadding
      mobileFullScreen
      containerClassName="pb-0"
    />
  );
};
